<template>
  <AnalysisPage :pageName="pageName" title="コース" :disabled="busy || !chart">
    <AnalysisContent :noSet="isMounted && !chart">
      <template #unit>
        <Unit :refetch="fetch" :disabled="busy" useRanking />
      </template>
      <template #graph>
        <AnalysisAggregate :busy="busy" :refetch="fetch" />
        <FrameTemplate v-if="isRanking(keepUnit)" :busy="busy" :nodata="!chart">
          <Chart type="BarChart" :data="chart" :options="rankingOptions" />
          <template #v-axis-unit>位</template>
          <template #h-axis-unit>件</template>
        </FrameTemplate>
        <FrameTemplate v-else :busy="busy" :nodata="!chart">
          <Chart
            type="LineChart"
            :data="chart"
            :options="menuOptions"
            :unit="keepUnit"
            :yearMonths="isMonthly(keepUnit) ? hAxis : []"
          />
          <template #v-axis-unit>数</template>
          <template #year-labels v-if="isMonthly(keepUnit)"><YearLabels :yearMonths="hAxis"/></template>
          <template #h-axis-unit>{{ unitById[keepUnit] }}</template>
        </FrameTemplate>
      </template>
      <template #table>
        <CourseRankingTable v-if="isRanking(keepUnit)" />
        <CourseMenuTable v-else />
      </template>
      <template v-if="!isRanking(keepUnit)" #action>
        <Button small fit class="open" @click="itemSelectModal = true">表示するコースを設定</Button>
      </template>
      <template #no-set>コースが設定されていません<br />設定のコース設定画面から設定してください</template>
    </AnalysisContent>
    <ItemSelectModal
      v-if="itemSelectModal"
      target="コース"
      :data="table.value"
      :selectedIds="table.selectedIds"
      @change="change"
      @close="itemSelectModal = false"
    />
  </AnalysisPage>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import AnalysisPage from '@/components/templates/AnalysisPage'
import AnalysisContent from '@/components/molecules/AnalysisContent'
import AnalysisAggregate, { Unit } from '@/components/organisms/AnalysisAggregate'
import Button from '@/components/atoms/Button'
import Chart, { FrameTemplate, YearLabels } from '@/components/molecules/Chart'
import CourseMenuTable from './Components/CourseMenuTable'
import CourseRankingTable from './Components/CourseRankingTable'
import ItemSelectModal from './Components/ItemSelectModal'

export default {
  name: 'AnalysisCourseMenu',
  components: {
    AnalysisPage,
    AnalysisContent,
    AnalysisAggregate,
    FrameTemplate,
    YearLabels,
    Unit,
    Button,
    Chart,
    CourseMenuTable,
    CourseRankingTable,
    ItemSelectModal,
  },
  data() {
    return {
      itemSelectModal: false,
      keepUnit: 0,
      isMounted: false,
    }
  },
  computed: {
    ...mapState({
      conditions: state => state.analysis.aggregate.conditions,
      unitById: state => state.analysis.unit.shortById,
      menuBusy: state => state.analysis.courseMenu.busy,
      rankingBusy: state => state.analysis.courseRanking.busy,
      table: state => state.analysis.courseMenu.table,
    }),
    ...mapGetters({
      query: 'analysis/aggregate/query',
      menuChart: 'analysis/courseMenu/chart',
      rankingChart: 'analysis/courseRanking/chart',
      viewWindowMaxValue: 'analysis/courseRanking/viewWindowMaxValue',
      header: 'analysis/courseMenu/header',
      hAxis: 'analysis/courseMenu/hAxis',
      menuChartColors: 'analysis/courseMenu/chartColors',
      rankingChartColors: 'analysis/colors/courseRanking',
      data: 'analysis/courseMenu/data',
      isRanking: 'analysis/unit/isRanking',
      isMonthly: 'analysis/unit/isMonthly',
    }),
    busy() {
      return this.menuBusy || this.rankingBusy
    },
    menuOptions() {
      return {
        colors: this.menuChartColors,
      }
    },
    rankingOptions() {
      return {
        colors: [this.rankingChartColors.other],
        annotations: {
          textStyle: {
            fontSize: 15,
            color: this.rankingChartColors.other,
            bold: true,
            opacity: 0.6,
          },
          stem: {
            color: 'transparent',
            length: 4,
          },
          alwaysOutside: true,
        },
        hAxis: {
          viewWindow: { max: this.viewWindowMaxValue },
          gridlines: { color: '#F2F2F2' },
          minorGridlines: { color: 'transparent' },
          baselineColor: '#ccc',
        },
        chartArea: {
          width: '100%',
          left: 49,
          top: 0,
          height: 318,
        },
        tooltip: { trigger: 'none' },
      }
    },
    chart() {
      return this.isRanking(this.keepUnit) ? this.rankingChart : this.menuChart(this.keepUnit)
    },
    pageName() {
      return this.isRanking(this.keepUnit) ? 'courseranking' : 'coursemenu'
    },
  },
  watch: {
    $route() {
      this.fetch()
    },
    busy(newValue) {
      if (!newValue) {
        this.keepUnit = this.conditions.unit
      }
    },
  },
  async mounted() {
    await this.fetch()
    this.isMounted = true
  },
  methods: {
    async fetch() {
      const { dispatch } = this.$store
      const { unit, startTerm, endTerm } = this.query

      if (this.isRanking(unit)) {
        await dispatch('analysis/courseRanking/get', { endTerm })
      } else {
        await dispatch('analysis/courseMenu/get', {
          unit,
          startTerm,
          endTerm,
        })
      }
    },
    change(ids) {
      this.$store.commit('analysis/courseMenu/setSelectedIds', ids)
    },
  },
}
</script>

<style scoped lang="postcss">
.open {
  padding: 0 16px;
}
</style>
